import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { EChecklistTaskStatus, ETaskId } from 'common/contracts';
import { useFormik } from 'formik';
import { EToasterStatus, showToast } from 'utils/showToast';

import { IntegrationSettingsData } from '../../Settings.types';
import { toastMessages } from '../DeveloperSettings.constants';
import {
  authPlayerSchema,
  eventsWebhookSchema,
  grantAwardSchema,
  otpSchema,
  personalizeSchema
} from '../DeveloperSettings.schemas';
import { EApiVersion, ESavingForm, ExtendedIntegrationData } from '../DeveloperSettings.types';
import {
  computeAwardingV2Ratio,
  determineSelectedAwardingVersion,
  determineTaskStatusByHasValue,
  determineTaskStatusByValue
} from '../helpers/developerSettings.helpers';

export enum ESaveResultStatus {
  SUCCESS = 'success',
  FAILED = 'failed'
}

export interface ISaveResult {
  status: ESaveResultStatus;
  url: string;
  version?: string;
}

export type OnSaveResult = (result: ISaveResult) => void;

interface IResolveUrlByVersionParams {
  selectedVersion: string;
  v2Url: string;
  v1Url: string;
}

const resolveUrlBySelectedVersion = ({
  selectedVersion,
  v2Url,
  v1Url
}: IResolveUrlByVersionParams): string => {
  if (selectedVersion === EApiVersion.V2) {
    return v2Url;
  }
  return v1Url;
};

interface ICallbackFormsParams {
  integrationData: ExtendedIntegrationData | undefined;
  updateIntegration: UseMutationResult<unknown, unknown, Partial<IntegrationSettingsData>>;
  getIntegration: UseQueryResult<IntegrationSettingsData, Error>;
  hasCategories: boolean;
  isWebStore: boolean;
  isWebCheckout: boolean;
  setTaskStatus: (taskId: ETaskId, status?: EChecklistTaskStatus) => void;
  setSavingForm: (form: ESavingForm) => void;
  onSaveResult?: OnSaveResult;
}

export const useAuthPlayerForm = ({
  integrationData,
  updateIntegration,
  getIntegration,
  hasCategories,
  isWebStore,
  setTaskStatus,
  setSavingForm,
  onSaveResult
}: Omit<ICallbackFormsParams, 'isWebCheckout'>) => {
  return useFormik({
    initialValues: {
      playersAuthWebhook: integrationData?.playersAuthWebhook || ''
    },
    enableReinitialize: true,
    validationSchema: authPlayerSchema,
    onSubmit: async (values) => {
      setSavingForm(ESavingForm.AUTH_PLAYER);
      updateIntegration.mutate(values, {
        onSuccess: () => {
          showToast({
            message: toastMessages.authPlayerSuccess,
            status: EToasterStatus.SUCCESS
          });
          getIntegration.refetch();
          if (hasCategories && isWebStore) {
            const taskStatus = determineTaskStatusByValue(values.playersAuthWebhook);
            setTaskStatus(ETaskId.CUSTOMIZE_AUTH_API, taskStatus);
          }
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({ status: ESaveResultStatus.SUCCESS, url: values.playersAuthWebhook });
        },
        onError: () => {
          showToast({
            message: toastMessages.authPlayerError,
            status: EToasterStatus.ERROR
          });
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({ status: ESaveResultStatus.FAILED, url: values.playersAuthWebhook });
        }
      });
    }
  });
};

export const useGrantAwardForm = ({
  integrationData,
  updateIntegration,
  getIntegration,
  hasCategories,
  isWebStore,
  isWebCheckout,
  setTaskStatus,
  setSavingForm,
  onSaveResult
}: ICallbackFormsParams) => {
  return useFormik({
    initialValues: {
      ordersReportingApiUrl: integrationData?.ordersReportingApiUrl || '',
      awardingV2ApiUrl: integrationData?.awardingV2ApiUrl || '',
      selectedAwardingVersion: determineSelectedAwardingVersion(integrationData?.awardingV2Ratio)
    },
    enableReinitialize: true,
    validationSchema: grantAwardSchema,
    onSubmit: async (values) => {
      setSavingForm(ESavingForm.GRANT_AWARD);

      const computedAwardingV2Ratio = computeAwardingV2Ratio(values.selectedAwardingVersion);

      const payload = {
        ordersReportingApiUrl: values.ordersReportingApiUrl,
        awardingV2ApiUrl: values.awardingV2ApiUrl,
        awardingV2Ratio: computedAwardingV2Ratio
      };

      const savedUrl = resolveUrlBySelectedVersion({
        selectedVersion: values.selectedAwardingVersion,
        v2Url: values.awardingV2ApiUrl,
        v1Url: values.ordersReportingApiUrl
      });

      updateIntegration.mutate(payload, {
        onSuccess: () => {
          showToast({
            message: toastMessages.grantAwardSuccess,
            status: EToasterStatus.SUCCESS
          });
          getIntegration.refetch();
          if (hasCategories && (isWebStore || isWebCheckout)) {
            const hasValue =
              values.ordersReportingApiUrl?.trim() || values.awardingV2ApiUrl?.trim();
            const taskStatus = determineTaskStatusByHasValue(!!hasValue);
            setTaskStatus(ETaskId.SETUP_AWARD_API, taskStatus);
          }
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({
            status: ESaveResultStatus.SUCCESS,
            url: savedUrl,
            version: values.selectedAwardingVersion
          });
        },
        onError: () => {
          showToast({
            message: toastMessages.grantAwardError,
            status: EToasterStatus.ERROR
          });
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({
            status: ESaveResultStatus.FAILED,
            url: savedUrl,
            version: values.selectedAwardingVersion
          });
        }
      });
    }
  });
};

export const usePersonalizeForm = ({
  integrationData,
  updateIntegration,
  getIntegration,
  hasCategories,
  isWebStore,
  setTaskStatus,
  setSavingForm,
  onSaveResult
}: Omit<ICallbackFormsParams, 'isWebCheckout'>) => {
  return useFormik({
    initialValues: {
      playerInfoSyncUrl: integrationData?.playerInfoSyncUrl || ''
    },
    enableReinitialize: true,
    validationSchema: personalizeSchema,
    onSubmit: async (values) => {
      setSavingForm(ESavingForm.PERSONALIZE);
      updateIntegration.mutate(values, {
        onSuccess: () => {
          showToast({
            message: toastMessages.personalizeSuccess,
            status: EToasterStatus.SUCCESS
          });
          getIntegration.refetch();
          if (hasCategories && isWebStore) {
            const taskStatus = determineTaskStatusByValue(values.playerInfoSyncUrl);
            setTaskStatus(ETaskId.SETUP_PERSONALIZATION_API, taskStatus);
          }
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({ status: ESaveResultStatus.SUCCESS, url: values.playerInfoSyncUrl });
        },
        onError: () => {
          showToast({
            message: toastMessages.personalizeError,
            status: EToasterStatus.ERROR
          });
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({ status: ESaveResultStatus.FAILED, url: values.playerInfoSyncUrl });
        }
      });
    }
  });
};

export const useEventsWebhookForm = ({
  integrationData,
  updateIntegration,
  getIntegration,
  setSavingForm,
  onSaveResult
}: Pick<ICallbackFormsParams, 'integrationData' | 'updateIntegration' | 'getIntegration' | 'setSavingForm' | 'onSaveResult'>) => {
  return useFormik({
    initialValues: {
      eventsWebhookUrl: integrationData?.eventsWebhookUrl || '',
      eventsV2WebhookUrl: integrationData?.eventsV2WebhookUrl || ''
    },
    enableReinitialize: true,
    validationSchema: eventsWebhookSchema,
    onSubmit: async (values) => {
      setSavingForm(ESavingForm.EVENTS_WEBHOOK);

      const savedUrl = values.eventsV2WebhookUrl || values.eventsWebhookUrl;

      updateIntegration.mutate(values, {
        onSuccess: () => {
          showToast({
            message: toastMessages.eventsWebhookSuccess,
            status: EToasterStatus.SUCCESS
          });
          getIntegration.refetch();
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({ status: ESaveResultStatus.SUCCESS, url: savedUrl });
        },
        onError: () => {
          showToast({
            message: toastMessages.eventsWebhookError,
            status: EToasterStatus.ERROR
          });
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({ status: ESaveResultStatus.FAILED, url: savedUrl });
        }
      });
    }
  });
};

export const useOtpForm = ({
  integrationData,
  updateIntegration,
  getIntegration,
  setSavingForm,
  onSaveResult
}: Pick<ICallbackFormsParams, 'integrationData' | 'updateIntegration' | 'getIntegration' | 'setSavingForm' | 'onSaveResult'>) => {
  return useFormik({
    initialValues: {
      otpOn: !!integrationData?.playersAuthentication?.otpOn,
      otpGenerateDeeplinkUrl: integrationData?.otpGenerateDeeplinkUrl || '',
      otpTextModel: integrationData?.playersAuthentication?.otpTextModel || ''
    },
    enableReinitialize: true,
    validationSchema: otpSchema,
    onSubmit: async (values) => {
      setSavingForm(ESavingForm.OTP);
      const playersAuthentication: IntegrationSettingsData['playersAuthentication'] = {
        otpOn: values.otpOn
      };
      if (values.otpTextModel) {
        playersAuthentication.otpTextModel = values.otpTextModel;
      }
      const payload: Partial<IntegrationSettingsData> = { playersAuthentication };
      if (values.otpGenerateDeeplinkUrl) {
        payload.otpGenerateDeeplinkUrl = values.otpGenerateDeeplinkUrl;
      }

      updateIntegration.mutate(payload, {
        onSuccess: () => {
          showToast({
            message: toastMessages.otpSuccess,
            status: EToasterStatus.SUCCESS
          });
          getIntegration.refetch();
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({ status: ESaveResultStatus.SUCCESS, url: values.otpGenerateDeeplinkUrl });
        },
        onError: () => {
          showToast({
            message: toastMessages.otpError,
            status: EToasterStatus.ERROR
          });
          setSavingForm(ESavingForm.NONE);
          onSaveResult?.({ status: ESaveResultStatus.FAILED, url: values.otpGenerateDeeplinkUrl });
        }
      });
    }
  });
};
